import { AmenityCell } from './AmenityCell';
import { RefreshButton } from './RefreshButton';
import { titleCaseCounty } from './labels';
import type { AdminMatrixCell } from './types';

export type AmenityCategoryKey = 'dog_park' | 'beach' | 'daycare' | 'bar';

interface CategoryMeta {
  display: string;
  pluralLower: string;
}

const CATEGORY_LABELS: Record<AmenityCategoryKey, CategoryMeta> = {
  dog_park: { display: 'Dog Parks', pluralLower: 'dog parks' },
  beach: { display: 'Beaches', pluralLower: 'beaches' },
  daycare: { display: 'Daycare', pluralLower: 'daycare' },
  bar: { display: 'Bars', pluralLower: 'dog-friendly bars' },
};

const CATEGORY_ORDER: AmenityCategoryKey[] = ['dog_park', 'beach', 'daycare', 'bar'];

/**
 * `<CountyRow>` — one row of the AmenityMatrix. Header cell with the
 * county name, four AmenityCells (one per category), and a per-row
 * refresh button at the right edge.
 *
 * pendingMap supports a few key shapes:
 *   `county:{slug}`   → bool, refresh-in-flight for this whole county
 *                        (disables every button in the row, including row-action)
 *   `category:{cat}`  → bool, refresh-in-flight for this category column
 *                        (disables the matching cell button across rows)
 */
export interface CountyRowProps {
  county: string;
  cells: Record<AmenityCategoryKey, AdminMatrixCell>;
  pendingMap: Record<string, boolean>;
  onRefreshCell: (category: AmenityCategoryKey) => void;
  onRefreshRow: () => void;
}

export function CountyRow({
  county,
  cells,
  pendingMap,
  onRefreshCell,
  onRefreshRow,
}: CountyRowProps) {
  const countyLabel = titleCaseCounty(county);
  const rowPending = pendingMap[`county:${county}`] === true;
  return (
    <tr>
      <th
        scope="row"
        className="p-3 text-left text-sm font-semibold text-foam-50/90 bg-ocean-900/60 border border-white/5"
      >
        {countyLabel}
      </th>
      {CATEGORY_ORDER.map((cat) => {
        const cellPending = rowPending || pendingMap[`category:${cat}`] === true;
        return (
          <AmenityCell
            key={cat}
            cell={cells[cat]}
            categoryLabel={CATEGORY_LABELS[cat].pluralLower}
            countyLabel={countyLabel}
            pending={cellPending}
            onRefresh={() => onRefreshCell(cat)}
          />
        );
      })}
      <td className="p-3 align-middle border border-white/5">
        <RefreshButton
          ariaLabel={`Refresh all categories in ${countyLabel}`}
          onClick={onRefreshRow}
          pending={rowPending}
        />
      </td>
    </tr>
  );
}
